<script lang="ts">
	import type { RestaurantsDynamic, RestaurantsSemiStatic } from "$lib/def/landing";
	import { Vertical } from "$lib/def/verticals";
	import type { BannerArrays, DisplayBanner } from "$lib/serv/banners";
	import type { Cache } from '$lib/serv/core/cache';
	import { fetch$ } from "$lib/serv/core/cache";
	import { CacheAgeMedium, SPKEYS, getDB, type DealsLoader } from "$lib/serv/food-landing";
	import { toQueryString } from "$lib/serv/locations";
	import Header from '$lib/ui/shared/Header.svelte';
	import { hasGeo } from "$lib/util/address";
	import { map } from "rxjs";
	import Tile from "./Tile.svelte";

	export let data: [
		Cache<RestaurantsSemiStatic> | null,
		Cache<RestaurantsDynamic> | null,
		DealsLoader | null,
	][] = []; // stores and restaurants are passed as different arrays
	export let searchParams: Record<string, string>;
	export let exitHref: string;

	$: params = Object.fromEntries(Object.entries(searchParams));
	$: wrapper$ = createStream(params);

	function createStream(p: typeof params) {
		const [id, type] = p.lp?.split('_') ?? [null, null];
		if (!id || !type) return null;
		if (!hasGeo(p)) return null;

		let q = toQueryString(p);
		if (p.vertical) {
			q += `&vertical=${p.vertical}`;
		}

		const url = new URL(`api://landing-items/v4/banners/${type}?${q}`);
		const obs = fetch$<BannerArrays>(url, SPKEYS, { db: getDB(), msMaxCacheAge: CacheAgeMedium });
		return obs.pipe(
			map((cache) => ({ ...cache, payload: cache.payload?.small ?? cache.payload?.large ?? cache.payload?.deluxe })),
			map((cache) => ({
				...cache,
				payload: cache.payload?.find((banner: DisplayBanner) => banner._uuid === id),
			})),
		)
	}

	function getSS(id: number) {
		for (const d of data) {
			const item = d[0]?.payload?.semi_static.find(r => r.id === id);
			if (item) return item;
		}
	}
	function getDyn(id: number)  {
		for (const d of data) {
			const item = d[1]?.payload?.dynamic.find(r => r.id === id);
			if (item) return item;
		}
	}
	function getDeal(id: number) {
		for (const d of data) {
			const item = d[2]?.restaurants.get(id);
			if (item) return item;
		}
	}

	function orderTiles(display: DisplayBanner) {
		const list = display.ids?.map(id => {
			const ss = getSS(id);
			const d = getDyn(id);
			const ds = getDeal(id);
			return { payload: { id }, semistatic: ss, dynamic: d, deals: ds };
		});
		// Order list by: (as per FEWEB-1123)
		// 1. open with delivery as option
		// 2. open with collect-only
		// 3. closed
		list?.sort((a, b) => {
			const aOpen = a.dynamic?.open && a.dynamic?.online;
			const bOpen = b.dynamic?.open && b.dynamic?.online;
			const aDelivery = a.dynamic?.delivery_option && a.dynamic?.delivery_option !== 'collect';
			const bDelivery = b.dynamic?.delivery_option && b.dynamic?.delivery_option !== 'collect';

			if (aOpen && !bOpen) return -1;
			if (!aOpen && bOpen) return 1;
			if (aDelivery && !bDelivery) return -1;
			if (!aDelivery && bDelivery) return 1;
			return 0;
		});
		return list ?? [];
	}
</script>

{#if $wrapper$}
	{@const cache = $wrapper$}
	{#if cache.loading}
		<h2 class="display-m bold loading">&nbsp;</h2>
		<div class="grid restaurants loading">
			{#each Array(6) as _}
				<a href={null} class="item">
					<div class="image"></div>
					<div class="text"></div>
				</a>
			{/each}
		</div>
	{:else if cache.error}
		<div class="message error body-m book">
			{cache.error?.friendly_message ?? cache.error}
		</div>
	{:else if cache.payload}
		{@const detail = cache.payload}
		{@const tiles = orderTiles(detail)}
		<Header back={exitHref} image={detail.display.main_image}>
			{#if detail.display.title}<h2 class="display-m bold">{detail.display.title}</h2>{/if}
			{#if detail.display.additional_data}<p class="body-m book">{detail.display.additional_data}</p>{/if}
			{#if detail.display.url_title}
				<div>
					<a href={detail.display.url} class="button secondary">{detail.display.url_title}</a>
				</div>
			{/if}
		</Header>

		<div class="m-v-l">
			<div class="restaurants grid" data-testid="lpTiles">
				{#each tiles as tile}
					{@const type = searchParams.vertical as Vertical ?? Vertical.Restaurant}
					<Tile {searchParams} tile={{ ...tile, type }} semistatic={tile.semistatic} dynamic={tile.dynamic} deals={tile.deals}/>
				{/each}
			</div>
		</div>
	{/if}
{/if}